import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Music, CheckCircle, XCircle } from 'lucide-react';
import { setPendingInvite } from '../utils/pendingInvite';
import { authClient } from '../lib/authClient';
import { clearSessionData } from '../utils/clearSessionData';

const slugify = str =>
  str.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

const inputStyle = (borderColor) => ({
  width: '100%',
  padding: '10px 14px',
  background: 'rgba(255,255,255,0.07)',
  border: `1px solid ${borderColor || 'rgba(255,255,255,0.15)'}`,
  borderRadius: 8,
  color: '#fff',
  fontSize: 15,
  outline: 'none',
  boxSizing: 'border-box',
});

export default function Onboarding() {
  const navigate = useNavigate();
  const [bandName, setBandName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugManual, setSlugManual] = useState(false);
  const [slugStatus, setSlugStatus] = useState(null); // null | 'checking' | 'available' | 'taken' | 'invalid'
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const checkTimer = useRef(null);
  const [inviteLink, setInviteLink] = useState('');

  useEffect(() => {
    if (!slugManual) setSlug(slugify(bandName));
  }, [bandName, slugManual]);

  useEffect(() => {
    if (checkTimer.current) clearTimeout(checkTimer.current);
    if (!slug) { setSlugStatus(null); return; }
    if (slug.length < 2 || !/^[a-z0-9]/.test(slug)) { setSlugStatus('invalid'); return; }
    setSlugStatus('checking');
    checkTimer.current = setTimeout(async () => {
      try {
        const res = await axios.get(`/api/onboarding/check-slug/${slug}`);
        setSlugStatus(res.data.available ? 'available' : 'taken');
      } catch {
        setSlugStatus(null);
      }
    }, 400);
  }, [slug]);

  const handleSlugChange = e => {
    setSlugManual(true);
    setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!bandName.trim() || slugStatus !== 'available') return;
    setSubmitting(true);
    setError('');
    try {
      await axios.post('/api/onboarding/setup', { band_name: bandName.trim(), band_slug: slug });
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
      setSubmitting(false);
    }
  };

  // Accepts a whole invite link (…/join#token) or just the token
  const joinWithInvite = e => {
    e.preventDefault();
    const token = inviteLink.trim().split('#').pop().trim();
    if (!token) return;
    setPendingInvite(token);
    navigate('/join');
  };

  const canSubmit = bandName.trim().length > 0 && slugStatus === 'available' && !submitting;

  const slugBorderColor = slugStatus === 'available'
    ? '#28a745'
    : (slugStatus === 'taken' || slugStatus === 'invalid')
      ? '#dc3545'
      : undefined;

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f0f23 0%, #1a1a3e 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 24,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    }}>
      <div style={{
        width: '100%',
        maxWidth: 440,
        background: 'rgba(255,255,255,0.05)',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 16,
        padding: '40px 36px',
      }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 56,
            height: 56,
            background: 'rgba(102,126,234,0.2)',
            borderRadius: 14,
            marginBottom: 16,
          }}>
            <Music size={28} color="#667eea" />
          </div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#fff' }}>
            Welcome to GigRequest
          </h1>
          <p style={{ margin: '8px 0 0', fontSize: 14, color: 'rgba(255,255,255,0.5)' }}>
            Let's set up your band or artist profile
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.7)', marginBottom: 6 }}>
              Band name
            </label>
            <input
              type="text"
              value={bandName}
              onChange={e => setBandName(e.target.value)}
              placeholder="The Rolling Stones"
              autoFocus
              style={inputStyle()}
            />
          </div>

          <div style={{ marginBottom: 24 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.7)', marginBottom: 6 }}>
              Band URL
            </label>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <span style={{
                position: 'absolute',
                left: 12,
                fontSize: 13,
                color: 'rgba(255,255,255,0.35)',
                pointerEvents: 'none',
                userSelect: 'none',
              }}>
                /gig/
              </span>
              <input
                type="text"
                value={slug}
                onChange={handleSlugChange}
                placeholder="the-rolling-stones"
                style={{ ...inputStyle(slugBorderColor), paddingLeft: 46, paddingRight: 36 }}
              />
              {slugStatus === 'available' && (
                <span style={{ position: 'absolute', right: 10 }}>
                  <CheckCircle size={16} color="#28a745" />
                </span>
              )}
              {(slugStatus === 'taken' || slugStatus === 'invalid') && (
                <span style={{ position: 'absolute', right: 10 }}>
                  <XCircle size={16} color="#dc3545" />
                </span>
              )}
            </div>
            {slugStatus === 'available' && (
              <p style={{ margin: '4px 0 0', fontSize: 12, color: '#28a745' }}>Available</p>
            )}
            {slugStatus === 'taken' && (
              <p style={{ margin: '4px 0 0', fontSize: 12, color: '#dc3545' }}>That URL is already taken</p>
            )}
            {slugStatus === 'invalid' && (
              <p style={{ margin: '4px 0 0', fontSize: 12, color: '#dc3545' }}>Use lowercase letters, numbers, and hyphens</p>
            )}
          </div>

          {error && (
            <p style={{ margin: '0 0 16px', fontSize: 13, color: '#dc3545', textAlign: 'center' }}>{error}</p>
          )}

          <button
            type="submit"
            disabled={!canSubmit}
            style={{
              width: '100%',
              padding: 12,
              background: canSubmit
                ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                : 'rgba(255,255,255,0.08)',
              border: 'none',
              borderRadius: 8,
              color: canSubmit ? '#fff' : 'rgba(255,255,255,0.25)',
              fontSize: 15,
              fontWeight: 600,
              cursor: canSubmit ? 'pointer' : 'not-allowed',
              transition: 'opacity 0.2s',
            }}
          >
            {submitting ? 'Setting up…' : 'Get started'}
          </button>
        </form>

        <form onSubmit={joinWithInvite} style={{ marginTop: 28, paddingTop: 20, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <label style={{ display: 'block', fontSize: 13, color: 'rgba(255,255,255,0.7)', marginBottom: 8 }}>
            Joining a band that's already on GigRequest? Paste the invite link they sent you.
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              value={inviteLink}
              onChange={e => setInviteLink(e.target.value)}
              placeholder="Invite link"
              style={inputStyle()}
            />
            <button type="submit" disabled={!inviteLink.trim()} style={{
              padding: '0 16px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.2)',
              background: 'transparent', color: '#fff', fontSize: 14, fontWeight: 600,
              cursor: inviteLink.trim() ? 'pointer' : 'not-allowed', opacity: inviteLink.trim() ? 1 : 0.4,
            }}>
              Join
            </button>
          </div>
        </form>

        <p style={{ margin: '24px 0 0', textAlign: 'center', fontSize: 13, color: 'rgba(255,255,255,0.5)' }}>
          Wrong account?{' '}
          <button
            type="button"
            onClick={() => authClient.signOut().then(clearSessionData).then(() => { window.location.href = '/sign-in'; })}
            style={{ background: 'none', border: 'none', padding: 0, color: '#8b9cf7', fontSize: 13, cursor: 'pointer', textDecoration: 'underline' }}
          >
            Sign out
          </button>
        </p>
      </div>
    </div>
  );
}
